import { AiWorkflow } from '../../types/content';

/** English mirror of ko/aiWorkflow.ts. At least one honest limitation is required. */
export const aiWorkflow: AiWorkflow = {
  headline: 'How I bring AI into the work',
  intro:
    'What AI changed is told in case C (AI analysis reports). This section is only about what I protect when bringing AI into the work.',
  caseId: 'C',
  how:
    'The core is not the model but context and harness. Data is contextualized through the data map (metric definitions, lineage, interpretation rules), and a harness lets AI read and write only within that context. The design is done in conversation with AI; what to protect is decided by me.',
  summary:
    'Inferences are labeled as estimates, example values are synthetic rather than real, and people appear only at department level. Agents never touch the warehouse directly. AI raises the speed; what to protect is decided by me.',
  guardrails: [
    {
      title: 'Inferences are labeled as estimates',
      description: 'Table descriptions and relations generated by the LLM are visually separated from vetted ones, so the catalog does not become a new source of misunderstanding.'
    },
    {
      title: 'Example values are synthetic only',
      description: 'Serialization structure is reproduced from real code, but every value and ID is fake. Documentation must not become a leak path.'
    },
    {
      title: 'People only at department level',
      description: 'Who looks at which data is aggregated by organization. Personal identifiers never enter the pipeline.'
    },
    {
      title: 'Agents never touch the warehouse directly',
      description: 'Read-only, partition filters required, results aggregated locally. Cost and exposure limits are held by a wrapper, not by human attention.'
    },
    {
      title: 'Anything needing approval stops and asks',
      description: 'Production access, destructive commands, and outbound transfers require a human confirmation even in auto-approve mode.'
    }
  ],
  limitations: [
    'AI analysis reports assist interpretation but do not replace judgment. The more plausible a report looks, the greater the risk it is quoted without verification, so moving anything into operations always goes through the consuming teams.',
    'Without separating inference from vetting, the catalog itself becomes a new source of misunderstanding. That is why the "estimate" label is enforced.',
    'Ambiguous data yields ambiguous inference. One input was derived data with interpretation already baked in, arranged to make results look favorable, and when AI used it the answers often diverged sharply from the plain facts. I structured the responses as JSON keys and added a step that re-verifies each item against the fact data, and later removed that input entirely in favor of verified facts.'
  ],
  diagram: `flowchart LR
  H[Human · question and judgment] --> AG[Coding agent]
  AG --> W[Query · schema wrapper<br/>read-only · partition guard]
  W --> DW[(Warehouse)]
  DW --> L[Local aggregation · only summaries enter context]
  L --> H
  SRC[Code · DDL · usage logs] --> X[LLM extraction]
  X --> T{Vetted?}
  T -->|yes| KG[Knowledge graph · confirmed]
  T -->|no| KG2[Knowledge graph · labeled estimate]
  KG & KG2 --> MAP[Static data map]`
};
